import { useSyncExternalStore } from "react";
import type { User } from "../../types/admin/UserTypes";
import { getAuthSession, subscribeAuthSession, type AuthSessionInfo } from "../../lib/http/session";

// Older builds kept patient_info/team_member_info here; purged on every profile write and sign-out.
const LEGACY_RELATIONS_KEY = "userRelations";

// The /me profile lives in memory only; no cookie carries any of it (contract §3, §16.1).
let profile: User | null = null;
const listeners = new Set<() => void>();

const publish = (next: User | null): void => {
	profile = next;
	listeners.forEach((listener) => listener());
};

const purgeLegacyRelations = (): void => {
	try {
		localStorage.removeItem(LEGACY_RELATIONS_KEY);
	} catch (error) {
		console.error("session.legacy_relations.purge_failed", { name: (error as Error)?.name });
	}
};

export const setSessionUser = (user: User): void => {
	purgeLegacyRelations();
	publish({ ...user });
};

// Profile edits patch the in-memory user; there is no profile copy to keep in sync anywhere else.
export const updateSessionUser = (patch: Partial<User>): void => {
	setSessionUser({ ...getSessionUser(), ...patch });
};

export const clearSessionUser = (): void => {
	purgeLegacyRelations();
	publish(null);
};

// The profile belongs to the identity session that loaded it: a signed-out session, or another account signed in from another tab, drops it.
const currentProfile = (): User | null => {
	if (!profile) return null;
	const auth = getAuthSession();
	if (auth.status === "anonymous") return null;
	const sessionUuid = auth.info?.user.uuid;
	return sessionUuid && sessionUuid !== profile.uuid ? null : profile;
};

export const isSessionUserLoaded = (): boolean => currentProfile() !== null;

const identityOnly = (info: AuthSessionInfo | null): User =>
	(info
		? { id: info.user.uuid, uuid: info.user.uuid, legacy_user_id: info.user.legacy_id, username: info.user.username, roles: info.roles.map((name) => ({ name })) }
		: {}) as User;

// Before /me resolves this is identity's own view (uuid, legacy id, username, role names) from GET /auth/session.
export const getSessionUser = (): User => {
	const current = currentProfile();
	return current ? { ...current } : identityOnly(getAuthSession().info);
};

const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	const unsubscribeAuth = subscribeAuthSession(listener);
	return () => {
		listeners.delete(listener);
		unsubscribeAuth();
	};
};

export const useSessionUser = (): User | null => useSyncExternalStore(subscribe, currentProfile, currentProfile);
